import { useEffect, useRef, useState } from "react";
import { apiBlobRequest } from "../../shared/api/client";

export function useProductionProductImage({ code, available }: { code: string | null; available: Set<string> }) {
  const element = useRef<HTMLSpanElement>(null);
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const exists = Boolean(code && available.has(code));
  const current = useRef({ code, exists, generation: 0 });
  if (current.current.code !== code || current.current.exists !== exists) {
    current.current = { code, exists, generation: current.current.generation + 1 };
  }
  const sourceOwner = useRef(-1), errorOwner = useRef(-1);
  useEffect(() => {
    const generation = current.current.generation;
    setSrc(""); setFailed(false);
    if (!exists || !code || !element.current) return;
    let active = true, objectUrl = "", started = false;
    const fail = () => { errorOwner.current = generation; setFailed(true); };
    const isCurrent = () => active && current.current.generation === generation;
    const load = () => {
      if (started || !isCurrent()) return;
      started = true;
      void apiBlobRequest(`/api/production/reports/product-image?${new URLSearchParams({ code })}`).then(blob => {
        if (!isCurrent()) return;
        if (!/^image\/(png|jpeg|gif|webp)$/.test(blob.type)) { fail(); return; }
        objectUrl = URL.createObjectURL(blob);
        sourceOwner.current = generation; setSrc(objectUrl);
      }).catch(() => { if (isCurrent()) fail(); });
    };
    const observer = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { load(); observer?.disconnect(); }
    }, { rootMargin: "80px" }) : null;
    if (observer) observer.observe(element.current); else load();
    return () => { active = false; observer?.disconnect(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [code, exists]);
  const onImageError = () => { errorOwner.current = current.current.generation; setFailed(true); };
  return { element, exists, src: sourceOwner.current === current.current.generation ? src : "", failed: errorOwner.current === current.current.generation && failed, onImageError };
}
